import { Box, Caption, Checkbox, LinkText, TextCell } from "@bcc-ui/ui-components";
import { t } from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import { CellProps, Column } from "react-table";

import { GradeBadge } from "./grade-badge/GradeBadge";
import { formatAssetValue } from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/estimated-value-card/estimatedValue";
import { GradingActionSelect } from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/grading-action-select/GradingActionSelect";
import { AssetPathKey } from "domain/asset-management/assetPathMapping";
import { AUTH_ASSET_EDIT } from "domain/authority";
import { isGrade } from "domain/grade";
import { Asset } from "services/asset-management/AssetService";
import { createTranslatorPath } from "services/pathTranslator";
import { userSessionService } from "services/user/UserSessionService";

import testIds from "testIds.json";

type SelectableAsset = Asset & { checked?: boolean };

export const createColumns = (
    onToggleAsset: (uuid: string) => void,
    onToggleAll: () => void,
    pageAssets: SelectableAsset[],
    onOpenAsset: (asset: SelectableAsset) => void
) => {
    const columns: Array<Column<SelectableAsset>> = [];
    const translatedPaths = createTranslatorPath(useTranslation, "assetPaths");
    const canEditAsset = userSessionService.userHasAllAuthorities([AUTH_ASSET_EDIT]);
    const allSelected = pageAssets.length > 0 && pageAssets.every((asset) => asset.checked === true);
    const someSelected = pageAssets.some((asset) => asset.checked === true) && !allSelected;

    columns.push({
        Header: () => (
            <Checkbox
                name="selectAllAssets"
                checked={allSelected}
                indeterminate={someSelected}
                disabled={!canEditAsset || pageAssets.length === 0}
                onChange={onToggleAll}
                hideLabelSection={true}
                testIds={{ input: testIds.workArea.asset.customView.value.table.selectAllCheckbox }}
            />
        ),
        accessor: "checked",
        disableSortBy: true,
        minWidth: 60,
        maxWidth: 60,
        Cell: ({ row: { original: asset } }: CellProps<SelectableAsset>) => (
            <Checkbox
                name={`selectAsset-${asset.uuid}`}
                checked={asset.checked === true}
                disabled={!canEditAsset}
                onChange={() => onToggleAsset(asset.uuid)}
                hideLabelSection={true}
                testIds={{ input: testIds.workArea.asset.customView.value.table.selectRowCheckbox }}
            />
        ),
        width: 60,
    });

    columns.push({
        Header: () => t("AssetManagement.assetId"),
        accessor: "assetId",
        Cell: ({ row: { original: asset } }: CellProps<SelectableAsset>) => (
            <TextCell>
                <Box>
                    <LinkText as={"button"} onClick={() => onOpenAsset(asset)}>
                        {asset.assetId}
                    </LinkText>
                </Box>
            </TextCell>
        ),
        width: 220,
        minWidth: 120,
    });

    columns.push({
        Header: () => t("AssetManagement.value.gradingTable.typeHeader"),
        accessor: "assetType",
        Cell: ({ row: { original: asset } }: CellProps<SelectableAsset>) => (
            <TextCell>
                <Box columnLayout gap="Xxs">
                    {asset.assetType}
                    {asset.model !== undefined && <Caption greyTextStyle>{asset.model}</Caption>}
                </Box>
            </TextCell>
        ),
        width: 240,
        minWidth: 120,
    });

    columns.push({
        Header: () => t("AssetManagement.value.gradingTable.gradeHeader"),
        accessor: AssetPathKey.BLANCCO_GRADE as keyof SelectableAsset,
        Cell: ({ cell: { value } }: CellProps<SelectableAsset>) => {
            if (!isGrade(value)) {
                return null;
            }
            return <GradeBadge grade={value} size={36} bold />;
        },
        width: 200,
        minWidth: 100,
    });

    columns.push({
        Header: () => translatedPaths(AssetPathKey.TOTAL_VALUE),
        accessor: AssetPathKey.ASSET_VALUE as keyof Asset,
        Cell: ({ row: { original: asset } }: CellProps<Asset>) =>
            formatAssetValue(asset[AssetPathKey.ASSET_VALUE], asset[AssetPathKey.POTENTIAL_UPLIFT_VALUE]) ?? null,
        width: 200,
        minWidth: 100,
    });

    columns.push({
        Header: () => t("AssetManagement.value.gradingTable.actionHeader"),
        accessor: "action",
        disableSortBy: true,
        Cell: ({ row: { original: asset } }: CellProps<SelectableAsset>) => (
            <GradingActionSelect key={asset.uuid} asset={asset} />
        ),
        width: 300,
        minWidth: 200,
    });
    return columns;
};
